import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Checks, MagnifyingGlass, Rows, SquaresFour, X } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../config/paths";
import FilterPill from "./FilterPill";
import SupplierTaskCard from "./detail/SupplierTaskCard";
import {
  buildAllSupplierTasks,
  isTaskOverdue,
  REPORTING_YEARS,
  TASK_DATA_TYPE_CONFIG,
  TASK_DATA_TYPES,
  TASK_STATUSES,
  COMPANY_WIDE,
} from "../suppliersMock";

const YEAR_OPTIONS = REPORTING_YEARS.map((y) => ({ value: String(y), label: String(y) }));
const TYPE_OPTIONS = TASK_DATA_TYPES.map((t) => ({ value: t, label: t }));
const STATUS_OPTIONS = [
  { value: TASK_STATUSES.NOT_STARTED, label: "Not started" },
  { value: TASK_STATUSES.IN_PROGRESS, label: "In progress" },
  { value: TASK_STATUSES.COMPLETE, label: "Complete" },
];

const STATUS_LABEL = {
  [TASK_STATUSES.NOT_STARTED]: "Not started",
  [TASK_STATUSES.IN_PROGRESS]: "In progress",
  [TASK_STATUSES.COMPLETE]: "Complete",
};
const STATUS_COLOR = {
  [TASK_STATUSES.NOT_STARTED]: "#FC8181",
  [TASK_STATUSES.IN_PROGRESS]: "#F59E0B",
  [TASK_STATUSES.COMPLETE]: "#22C55E",
};

// Data Tasks tab — every supplier data task across the org, as cards or a
// table, with search + filters and multi-select.
function DataTasksTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const allTasks = useMemo(() => buildAllSupplierTasks(), []);

  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [viewType, setViewType] = useState("grid"); // "grid" | "list"
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Apply deep-link filter params (set by the Status cards), then consume them
  // so they don't keep re-overriding manual filter changes. Runs whenever the
  // deep-link params change.
  const deepLink = `${searchParams.get("dtYear") || ""}|${searchParams.get("dtStatus") || ""}|${searchParams.get("dtOverdue") || ""}`;
  useEffect(() => {
    const yearParam = searchParams.get("dtYear");
    const statusParam = searchParams.get("dtStatus");
    const overdueParam = searchParams.get("dtOverdue") === "1";
    if (!yearParam && !statusParam && !overdueParam) return;
    setYearFilter(yearParam ? [{ value: yearParam, label: yearParam }] : []);
    setStatusFilter(statusParam ? statusParam.split(",").filter(Boolean).map((v) => ({ value: v, label: v })) : []);
    setOverdueOnly(overdueParam);
    const next = new URLSearchParams(searchParams);
    ["dtYear", "dtStatus", "dtOverdue"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const clearOverdue = () => setOverdueOnly(false);

  const supplierOptions = useMemo(() => {
    const seen = new Map();
    allTasks.forEach((t) => { if (!seen.has(t.supplierId)) seen.set(t.supplierId, t.supplierName); });
    return [...seen.entries()].map(([value, label]) => ({ value: String(value), label }));
  }, [allTasks]);
  const [supplierFilter, setSupplierFilter] = useState([]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const years = new Set(yearFilter.map((y) => y.value));
    const types = new Set(typeFilter.map((t) => t.value));
    const statuses = new Set(statusFilter.map((s) => s.value));
    const suppliers = new Set(supplierFilter.map((s) => s.value));
    return allTasks.filter((t) => {
      if (term && !`${t.supplierName} ${t.dataType} ${t.year} ${t.division || ""}`.toLowerCase().includes(term)) return false;
      if (years.size && !years.has(String(t.year))) return false;
      if (types.size && !types.has(t.dataType)) return false;
      if (statuses.size && !statuses.has(t.status)) return false;
      if (suppliers.size && !suppliers.has(String(t.supplierId))) return false;
      if (overdueOnly && !isTaskOverdue(t)) return false;
      return true;
    });
  }, [allTasks, search, yearFilter, typeFilter, statusFilter, supplierFilter, overdueOnly]);

  const openTask = (t) =>
    navigate(
      routes.supplyChainSupplierTask
        .replace(":supplierId", t.supplierId)
        .replace(":taskId", t.id)
    );

  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleSelectMode = () => {
    setSelectMode((m) => !m);
    setSelected(new Set());
  };

  const hasSearch = search.trim().length > 0;

  return (
    <Box mt={6}>
      {/* Filter row */}
      <Flex gap={2} mb={4} flexWrap="wrap" align="center">
        <InputGroup size="sm" maxW="240px">
          <InputLeftElement pointerEvents="none"><MagnifyingGlass size={14} color="#A0AEC0" /></InputLeftElement>
          <Input
            borderRadius="full"
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            borderColor={hasSearch ? "lime" : "gray.200"}
            bg={hasSearch ? "lightLime" : "white"}
            _hover={{ borderColor: "lime" }}
            _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
          />
        </InputGroup>
        <FilterPill label="Year" options={YEAR_OPTIONS} selected={yearFilter} onChange={setYearFilter} />
        <FilterPill label="Data type" options={TYPE_OPTIONS} selected={typeFilter} onChange={setTypeFilter} />
        <FilterPill label="Status" options={STATUS_OPTIONS} selected={statusFilter} onChange={setStatusFilter} />
        <FilterPill label="Supplier" options={supplierOptions} selected={supplierFilter} onChange={setSupplierFilter} />
        {overdueOnly && (
          <HStack
            as="button"
            spacing={1.5}
            bg="red.50"
            color="red.600"
            borderRadius="full"
            border="1px solid"
            borderColor="red.200"
            px={3}
            h="32px"
            fontSize="13px"
            fontWeight="600"
            onClick={clearOverdue}
          >
            <Text>Overdue only</Text>
            <X size={12} weight="bold" />
          </HStack>
        )}
      </Flex>

      {/* Count + view toggle + select */}
      <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">{filtered.length} task{filtered.length === 1 ? "" : "s"}</Text>
        <HStack spacing={4}>
          <Button
            variant="link"
            size="sm"
            color={selectMode ? "limeShade.700" : "gray.500"}
            fontWeight="600"
            leftIcon={<Checks size={16} />}
            onClick={toggleSelectMode}
            _hover={{ textDecoration: "none", color: "limeShade.700" }}
          >
            {selectMode ? `Selected ${selected.size}` : "Select multiple"}
          </Button>
          <Flex bg="gray.100" borderRadius="full" p="3px">
            <Button size="sm" borderRadius="full" bg={viewType === "list" ? "darkTeal" : "transparent"} color={viewType === "list" ? "white" : "gray.500"} _hover={{ bg: viewType === "list" ? "darkTeal" : "gray.300" }} rightIcon={<Rows size={16} />} fontWeight="600" fontSize="13px" px={4} onClick={() => setViewType("list")}>List</Button>
            <Button size="sm" borderRadius="full" bg={viewType === "grid" ? "darkTeal" : "transparent"} color={viewType === "grid" ? "white" : "gray.500"} _hover={{ bg: viewType === "grid" ? "darkTeal" : "gray.300" }} rightIcon={<SquaresFour size={16} />} fontWeight="600" fontSize="13px" px={4} onClick={() => setViewType("grid")}>Grid</Button>
          </Flex>
        </HStack>
      </Flex>

      {filtered.length === 0 ? (
        <Box border="1px dashed" borderColor="gray.200" borderRadius="md" py={12} textAlign="center">
          <Text color="gray.400" fontSize="14px">No data tasks match your filters.</Text>
        </Box>
      ) : viewType === "grid" ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {filtered.map((t) => (
            <Box key={t.id} position="relative">
              {selectMode && (
                <Box position="absolute" top={3} left={3} zIndex={1} onClick={(e) => e.stopPropagation()}>
                  <Checkbox isChecked={selected.has(t.id)} onChange={() => toggleSelected(t.id)} colorScheme="limeShade" bg="white" borderRadius="sm" />
                </Box>
              )}
              <SupplierTaskCard
                task={t}
                onOpen={() => (selectMode ? toggleSelected(t.id) : openTask(t))}
              />
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                {selectMode && <Th py={3} px={4} width="40px" />}
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Supplier</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Data type</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Period</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Division</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((t, idx) => {
                const typeCfg = TASK_DATA_TYPE_CONFIG[t.dataType] || {};
                return (
                  <Tr key={t.id} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100" _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => (selectMode ? toggleSelected(t.id) : openTask(t))}>
                    {selectMode && (
                      <Td py={3} px={4} onClick={(e) => e.stopPropagation()}>
                        <Checkbox isChecked={selected.has(t.id)} onChange={() => toggleSelected(t.id)} colorScheme="limeShade" />
                      </Td>
                    )}
                    <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="600">{t.supplierName}</Td>
                    <Td py={3} px={4} fontSize="14px">
                      <HStack spacing={1.5}>
                        <Box w="9px" h="9px" borderRadius="full" bg={typeCfg.text || "gray.400"} />
                        <Text color="gray.700">{t.dataType}</Text>
                      </HStack>
                    </Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700">{t.year} {t.quarter !== "ALL" ? t.quarter : ""}</Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700">{t.division || COMPANY_WIDE}</Td>
                    <Td py={3} px={4} fontSize="14px">
                      <HStack spacing={2}>
                        <Box w="8px" h="8px" borderRadius="full" bg={STATUS_COLOR[t.status]} />
                        <Text color="gray.700">{STATUS_LABEL[t.status]}</Text>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default DataTasksTab;
